import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { renderBoardMarkup } from '../../shared/board-markup.js';
import { EmbedFrame } from './EmbedFrame.jsx';

// The Board — bulletin board, Phase 1 (CJ 2026-07-31). Replaces Chat.
// Walls (General + per-location), posts by topic, read-receipts, Suggestion
// upvotes, immediate comments, and manager moderation. Broadcast email (Phase 2)
// and Ask Empire auto-answers (Phase 3) render here when they land — assistant
// comments already display if present. Spec: docs/BULLETIN-BOARD-SPEC.md.

const TOPIC_COLOR = {
  Announcement: '#3f6f86', Training: '#2c6247', Schedule: '#7a5cc0', Kudos: '#b06f26',
  Maintenance: '#9c5a34', Question: '#2c6247', Suggestion: '#b0842f', 'For Managers': '#8a6d1f',
};

// Docket D97 (2026-08-01). api() throws with the raw response body as its
// message — usually `{"error":"..."}` — so surface the useful part instead of
// showing a staffer a JSON blob. The permission case gets its own wording
// because that is the one that actually happened: board_post named a role no
// user had, every barista got a 403, and the screen sat on "Loading the
// Board…" forever because the error was set but never rendered (D91).
export function friendlyBoardError(e) {
  const raw = (e && e.message) || '';
  let msg = raw;
  try { msg = JSON.parse(raw).error || raw; } catch (_) { /* not JSON */ }
  // Two different denial shapes reach here, and the Board's FIRST call hits the
  // second one — matching only "Not allowed" showed a staffer the bare word
  // "Forbidden", which is how this looked when first verified in a browser:
  //   requireCapability → {"error":"Forbidden","required":"board_post"}   (/walls)
  //   boardMember       → {"error":"Not allowed."}                        (/posts)
  if (/not allowed|forbidden/i.test(msg)) {
    return "You don't have access to the Board. If that's unexpected, tell your manager — it's a permissions setting, not something you did.";
  }
  if (/not authenticated/i.test(msg)) {
    return 'Your session has expired. Sign out and back in.';
  }
  return msg || 'The Board could not be loaded.';
}

export function BoardView({ user, locations }) {
  const headers = { 'x-user-id': String(user.id) };
  const [walls, setWalls] = useState(null);
  const [topics, setTopics] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [wall, setWall] = useState(null);          // location_id (null = General)
  const [topic, setTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [compose, setCompose] = useState(null);    // compose form state or null
  const [ackModal, setAckModal] = useState(null);  // { post, roster }
  const sigRef = useRef('');                        // last-rendered feed signature (silent-poll guard)

  async function loadWalls() {
    setErr('');
    try {
      const r = await api('GET', '/api/board/walls', null, headers);
      setWalls(r.walls); setTopics(r.topics); setIsManager(r.isManager);
    } catch (e) { setErr(friendlyBoardError(e)); }
  }
  async function loadPosts({ silent = false } = {}) {
    if (!silent) setLoading(true);
    setErr('');
    try {
      const qs = new URLSearchParams();
      if (wall != null) qs.set('location_id', wall);
      if (topic) qs.set('topic', topic);
      const r = await api('GET', `/api/board/posts${qs.toString() ? '?' + qs : ''}`, null, headers);
      const next = r.posts || [];
      // Only re-render when the feed actually changed — a background poll that
      // replaces identical state causes visible churn (CJ, 2026-07-31).
      const sig = JSON.stringify(next);
      if (!silent || sig !== sigRef.current) { sigRef.current = sig; setPosts(next); }
    } catch (e) { if (!silent) { setErr(friendlyBoardError(e)); setPosts([]); sigRef.current = ''; } }
    finally { if (!silent) setLoading(false); }
  }
  useEffect(() => { loadWalls(); }, []);
  useEffect(() => { if (walls) loadPosts(); }, [wall, topic, walls]);
  // Event-driven refresh, NOT blind timer churn (CJ, 2026-07-31): the board
  // checks quietly in the background but only re-renders when the feed actually
  // changed — so an idle board sits perfectly still, while a new comment
  // reaches anyone still viewing. Paused while you're
  // composing/acking or the tab is hidden.
  useEffect(() => {
    if (!walls) return;
    const t = setInterval(() => {
      if (!compose && !ackModal && document.visibilityState === 'visible') loadPosts({ silent: true });
    }, 25000);
    return () => clearInterval(t);
  }, [walls, wall, topic, compose, ackModal]);

  async function act(fn, reloadAfter = true) {
    try { await fn(); if (reloadAfter) await loadPosts(); }
    catch (e) { let m = e.message || 'Failed'; try { m = JSON.parse(m).error || m; } catch (_) {} alert(m); }
  }
  const acknowledge = (id) => act(() => api('POST', `/api/board/posts/${id}/ack`, {}, headers));
  const upvote = (id) => act(() => api('POST', `/api/board/posts/${id}/upvote`, {}, headers));
  const addComment = (id, val) => { if (!val.trim()) return; act(() => api('POST', `/api/board/posts/${id}/comments`, { body_md: val }, headers)); };
  const delComment = (id) => act(() => api('DELETE', `/api/board/comments/${id}`, null, headers));
  const confirmAnswer = (id) => act(() => api('POST', `/api/board/comments/${id}/confirm`, {}, headers));
  const editAnswer = (id, bodyMd) => act(() => api('PATCH', `/api/board/comments/${id}`, { body_md: bodyMd }, headers));
  const delPost = (id) => { if (confirm('Delete this post?')) act(() => api('DELETE', `/api/board/posts/${id}`, null, headers)); };
  // Rethrows so PostEditor keeps the draft on screen when the save fails —
  // act() swallows into an alert, which would otherwise close the editor and
  // lose what they typed.
  const editPost = async (id, patch) => {
    await api('PATCH', `/api/board/posts/${id}`, patch, headers);
    await loadPosts();
  };
  const togglePin = (p) => act(() => api('PATCH', `/api/board/posts/${p.id}`, { pinned: !p.pinned }, headers));
  const toggleComments = (p) => act(() => api('PATCH', `/api/board/posts/${p.id}`, { comments_enabled: !p.comments_enabled }, headers));
  async function openAcks(post) {
    try { const r = await api('GET', `/api/board/posts/${post.id}/acks`, null, headers); setAckModal({ post, roster: r.roster }); }
    catch (e) { alert(e.message || 'Failed'); }
  }

  const wallLabel = walls && wall != null ? (walls.find((w) => w.location_id === wall)?.label || 'Wall') : 'General';

  // D97: this used to be `if (!walls) return <Loading/>` with no error branch.
  // loadWalls() set `err` correctly, but walls stayed null, so the early return
  // won and the failure rendered as an endless spinner — it did not look
  // broken, it looked slow. That is why a 403 affecting 32 baristas went
  // unreported for a day. A failed load must say so, and offer a way back.
  if (!walls && err) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>The Board didn’t load</div>
        <div className="text-xs text-red-700" style={{ marginBottom: 10 }}>{err}</div>
        <button className="btn-secondary text-xs" onClick={loadWalls}>Try again</button>
      </div>
    );
  }
  if (!walls) return <div className="card text-sm text-gray-500">Loading the Board…</div>;

  return (
    <div className="space-y-3">
      {/* Wall tabs */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
        {walls.map((w) => (
          <button key={w.key} onClick={() => { setWall(w.location_id); setTopic(null); }}
            style={{ border: '1px solid #d0d4d9', borderRadius: 999, padding: '5px 14px', fontSize: 13, fontWeight: 600,
                     whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
                     background: w.location_id === wall ? 'var(--forest)' : '#fff', color: w.location_id === wall ? '#fff' : '#37474f',
                     borderColor: w.location_id === wall ? 'var(--forest)' : '#d0d4d9' }}>
            {w.label}
          </button>
        ))}
      </div>

      {/* header + new post */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{wallLabel}</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>{wall == null ? 'Everyone, all cafés' : 'Café team'}</div>
        </div>
        <button className="btn-primary" onClick={() => setCompose({ location_id: wall, topic: 'Announcement', title: '', body_md: '', managers_only: false, require_ack: false, format: 'markdown', embed_html: '' })}>
          ＋ New post
        </button>
      </div>

      {/* topic filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {topics.map((t) => (
          <button key={t} onClick={() => setTopic(topic === t ? null : t)}
            style={{ border: '1px solid #d0d4d9', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                     cursor: 'pointer', fontFamily: 'inherit',
                     background: topic === t ? '#fef3d9' : '#fff', color: topic === t ? '#8a6d1f' : '#757575',
                     borderColor: topic === t ? '#e0c188' : '#d0d4d9' }}>
            {t}
          </button>
        ))}
      </div>

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828' }}><div className="text-xs text-red-700">{err}</div></div>}
      {loading && <div className="card text-sm text-gray-500">Loading…</div>}
      {!loading && posts.length === 0 && <div className="card text-sm text-gray-500">No posts here yet. Tap <b>＋ New post</b> to start.</div>}

      {posts.map((p) => (
        <PostCard key={p.id} post={p} user={user} isManager={isManager}
          onAck={acknowledge} onUpvote={upvote} onComment={addComment} onDelComment={delComment}
          onDelPost={delPost} onEditPost={editPost} onTogglePin={togglePin} onToggleComments={toggleComments} onOpenAcks={openAcks}
          onConfirmAnswer={confirmAnswer} onEditAnswer={editAnswer} />
      ))}

      {compose && <ComposeModal state={compose} setState={setCompose} walls={walls} topics={topics} isManager={isManager}
        user={user} onDone={() => { setCompose(null); loadPosts(); }} />}
      {ackModal && <AckModal data={ackModal} user={user} onClose={() => setAckModal(null)} />}

      <style>{`
        .board-body p { margin: .4em 0; line-height: 1.55; }
        .board-body ul { margin: .4em 0; padding-left: 1.2em; }
        .board-body li { margin: .15em 0; }
        .board-body a { color: var(--forest); font-weight: 600; }
        .board-body h4 { font-family: var(--serif, Georgia); font-size: 1.05em; margin: .3em 0; }
        @media (max-width: 640px) { .board-compose-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

function PostCard({ post: p, user, isManager, onAck, onUpvote, onComment, onDelComment, onDelPost, onEditPost, onTogglePin, onToggleComments, onOpenAcks, onConfirmAnswer, onEditAnswer }) {
  const [cval, setCval] = useState('');
  const [editing, setEditing] = useState(false);
  const tc = TOPIC_COLOR[p.topic] || '#757575';
  const badge = (label, color, bg) => <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', padding: '2px 7px', borderRadius: 5, color, background: bg, whiteSpace: 'nowrap' }}>{label}</span>;
  return (
    <div className="card" style={{ borderLeft: p.managers_only ? '3px solid var(--gold)' : (p.pinned ? '3px solid var(--gold)' : undefined) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#e8f5e9', color: 'var(--forest)', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {(p.author.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{p.author.name}</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            {p.author.role} · {rel(p.created_at)}
            {p.edited && (p.edited_by
              ? ` · edited by ${p.edited_by.name}`
              : ' · edited')}
          </div>
        </div>
        {/* Was wrapped in `isManager &&`, which hid Delete from a barista on
            their OWN post even though can_edit said yes. The per-action flags
            already carry the rule; the outer manager check only contradicted
            them. */}
        {(p.can_edit || p.can_moderate) && (
          <div style={{ display: 'flex', gap: 6 }}>
            {p.can_moderate && <button onClick={() => onTogglePin(p)} title={p.pinned ? 'Unpin' : 'Pin'} style={miniBtn}>{p.pinned ? '📌' : 'Pin'}</button>}
            {p.can_edit && !editing && <button onClick={() => setEditing(true)} title="Edit post" style={miniBtn}>Edit</button>}
            {p.can_edit && <button onClick={() => onDelPost(p.id)} title="Delete" style={{ ...miniBtn, color: '#c62828' }}>✕</button>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
        {badge(p.topic, tc, '#f4f1ea')}
        {p.pinned && badge('📌 Pinned', '#a55c33', '#f5e3d6')}
        {p.emailed && badge('✉ Emailed', '#3f6f86', '#e2edf1')}
        {p.managers_only && badge('Managers only', '#8a6d1f', '#f5e9d0')}
      </div>
      {editing ? (
        <PostEditor post={p} onCancel={() => setEditing(false)}
          onSave={async (id, patch) => { await onEditPost(id, patch); setEditing(false); }} />
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--serif, Georgia)', marginBottom: 2 }}>{p.title}</div>
          {p.body_html && <div className="board-body" style={{ fontSize: 14, color: '#212121' }} dangerouslySetInnerHTML={{ __html: p.body_html }} />}
          {p.format === 'embed' && p.embed_html && <div style={{ marginTop: 8 }}><EmbedFrame html={p.embed_html} title={p.title} /></div>}
        </>
      )}

      {/* Suggestion upvotes */}
      {p.topic === 'Suggestion' && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => onUpvote(p.id)} style={{ border: `1px solid ${p.upvotes.mine ? 'var(--forest)' : '#d0d4d9'}`, background: p.upvotes.mine ? '#e8f5e9' : '#fff', color: p.upvotes.mine ? 'var(--forest)' : '#555', borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            ▲ {p.upvotes.count} {p.upvotes.mine ? 'Upvoted' : 'Upvote'}
          </button>
        </div>
      )}

      {/* require-ack strip */}
      {p.ack && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #eee', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {p.ack.mine
            ? <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>✓ You acknowledged</span>
            : <button onClick={() => onAck(p.id)} style={{ background: 'var(--gold)', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Acknowledge</button>}
          <div style={{ flex: 1, minWidth: 90, height: 7, background: '#eee', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${p.ack.total ? Math.round(p.ack.count / p.ack.total * 100) : 0}%`, background: 'var(--forest)' }} />
          </div>
          <span onClick={() => p.can_moderate && onOpenAcks(p)} style={{ fontSize: 12, color: p.can_moderate ? 'var(--forest)' : '#757575', fontWeight: 600, cursor: p.can_moderate ? 'pointer' : 'default', textDecoration: p.can_moderate ? 'underline' : 'none' }}>
            {p.ack.count} of {p.ack.total} read{p.can_moderate ? ' · see who' : ''}
          </span>
        </div>
      )}

      {/* comments */}
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: '#9e9e9e' }}>💬 {p.comments.filter((c) => c.author_kind !== 'assistant').length} comment{p.comments.filter((c) => c.author_kind !== 'assistant').length !== 1 ? 's' : ''}{p.comments.some((c) => c.author_kind === 'assistant') ? ' · 1 auto-answer' : ''}</span>
        {p.can_moderate && <button onClick={() => onToggleComments(p)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--forest)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{p.comments_enabled ? 'Turn off comments' : 'Turn on comments'}</button>}
      </div>
      <div style={{ marginTop: 6, borderTop: '1px solid #f0f0f0', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {p.comments.map((c) => <Comment key={c.id} c={c} p={p} isManager={isManager} onDel={onDelComment} onConfirm={onConfirmAnswer} onEdit={onEditAnswer} />)}
        {p.comments_enabled
          ? <div style={{ display: 'flex', gap: 6 }}>
              <input value={cval} onChange={(e) => setCval(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { onComment(p.id, cval); setCval(''); } }}
                placeholder="Add a comment…" style={{ flex: 1, border: '1px solid #d0d4d9', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontFamily: 'inherit' }} />
              <button onClick={() => { onComment(p.id, cval); setCval(''); }} className="btn-secondary" style={{ fontSize: 12 }}>Post</button>
            </div>
          : <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic' }}>Comments are turned off for this post.</div>}
      </div>
    </div>
  );
}

// Edit a post in place (CJ 2026-08-05). Open to the original poster, a manager
// or an owner — the server enforces the same rule; `p.can_edit` just decides
// whether the button is drawn.
//
// Deliberately a top-level component, NOT one defined inside PostCard's render.
// A component declared in a render body is a new type on every keystroke, so
// React unmounts and remounts it and the field loses focus after each character
// — the bug that cost an afternoon on the compose modal.
function PostEditor({ post: p, onSave, onCancel }) {
  const [title, setTitle] = useState(p.title || '');
  const [body, setBody] = useState(p.body_md || '');
  const [embed, setEmbed] = useState(p.embed_html || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEmbed = p.format === 'embed';
  const dirty = title !== (p.title || '') || body !== (p.body_md || '') || (isEmbed && embed !== (p.embed_html || ''));
  const canSave = !!title.trim() && dirty && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true); setErr('');
    const patch = { title: title.trim(), body_md: body };
    if (isEmbed) patch.embed_html = embed;
    try {
      await onSave(p.id, patch);
    } catch (e) {
      // Show it here rather than in an alert: an alert plus a closed editor
      // would throw away everything they typed.
      let m = e.message || 'Could not save that.';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setSaving(false); }
  }

  const field = { width: '100%', border: '1px solid #d0d4d9', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 2px' }}>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
        aria-label="Post title"
        style={{ ...field, fontWeight: 700, fontFamily: 'var(--serif, Georgia)' }} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
        aria-label="Post body"
        placeholder="Write the post…" style={{ ...field, resize: 'vertical', lineHeight: 1.5 }} />
      {isEmbed && (
        <textarea value={embed} onChange={(e) => setEmbed(e.target.value)} rows={6}
          aria-label="Embedded artifact HTML"
          placeholder="Paste the HTML artifact…"
          style={{ ...field, resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }} />
      )}
      {!title.trim() && <div style={{ fontSize: 12, color: '#a8323a' }}>A title is required.</div>}
      {err && <div style={{ fontSize: 12, color: '#a8323a', background: '#faeceb', borderRadius: 6, padding: '6px 10px' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={!canSave}
          style={{ background: canSave ? 'var(--forest)' : '#e0e0e0', color: '#fff', border: 'none', borderRadius: 7,
                   padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: canSave ? 'pointer' : 'default', fontFamily: 'inherit' }}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button onClick={onCancel} disabled={saving}
          style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 7, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#757575', cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Comment({ c, p, isManager, onDel, onConfirm, onEdit }) {
  const assistant = c.author_kind === 'assistant';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body_md || '');
  // Manager tools on an Ask Empire answer live behind can_moderate — an
  // employee never sees Edit/Endorse/Remove, only the answer + disclaimer.
  const canManageAnswer = assistant && p.can_moderate;
  const saveEdit = () => { if (draft.trim()) { onEdit(c.id, draft.trim()); setEditing(false); } };
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ width: 26, height: 26, borderRadius: '50%', background: assistant ? 'var(--forest)' : '#f4f6f5', color: assistant ? '#fff' : '#37474f', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {assistant ? 'EA' : (c.author.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
      </div>
      <div style={{ flex: 1, background: assistant ? '#e2ede1' : '#f4f6f5', border: assistant ? '1px solid #bcd6c4' : 'none', borderRadius: 9, padding: '6px 10px', fontSize: 13 }}>
        <div>
          <b style={{ fontSize: 12 }}>{c.author.name}</b>
          {assistant && <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', color: 'var(--forest)', background: '#fff', borderRadius: 4, padding: '1px 5px', marginLeft: 6 }}>{c.assistant_gap ? 'no match' : 'auto-answer'}</span>}
          <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 6 }}>{c.author.role !== 'assistant' ? c.author.role + ' · ' : ''}{rel(c.created_at)}{c.edited_by ? ' · edited' : ''}</span>
          {(isManager && !assistant && p.can_moderate) && <button onClick={() => onDel(c.id)} title="Delete comment" style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 12, float: 'right' }}>✕</button>}
        </div>
        {editing
          ? <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5}
              style={{ width: '100%', marginTop: 4, fontSize: 13, fontFamily: 'inherit', borderRadius: 6, border: '1px solid #bcd6c4', padding: 6, boxSizing: 'border-box' }} />
          : <div className="board-body" dangerouslySetInnerHTML={{ __html: c.body_html }} />}
        {assistant && c.source_label && <a href={c.source_url || '#'} onClick={(e) => !c.source_url && e.preventDefault()} style={{ fontSize: 12, color: 'var(--forest)', fontWeight: 600 }}>📄 {c.source_label}</a>}
        {assistant && (c.confirmed_by
          ? <div style={{ fontSize: 11, color: 'var(--forest)', fontWeight: 700, marginTop: 3 }}>✓ Endorsed by {c.confirmed_by}{c.kb_proposal_id ? ' · sent to the Knowledge Base for review' : ''}</div>
          : <div style={{ fontSize: 11, color: '#9e9e9e', fontStyle: 'italic', marginTop: 3 }}>🤖 Auto-generated from Empire’s materials · double-check with a manager if it matters</div>)}
        {canManageAnswer && !c.confirmed_by && (
          editing
            ? <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button onClick={saveEdit} style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                <button onClick={() => { setDraft(c.body_md || ''); setEditing(false); }} style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#757575', cursor: 'pointer' }}>Cancel</button>
              </div>
            : <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                <button onClick={() => { setDraft(c.body_md || ''); setEditing(true); }} style={{ background: '#fff', border: '1px solid #bcd6c4', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: 'var(--forest)', cursor: 'pointer' }}>Edit response</button>
                <button onClick={() => onConfirm(c.id)} title="Mark accurate and send to the Knowledge Base for review" style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Endorse</button>
                <button onClick={() => onDel(c.id)} title="Remove this answer" style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#757575', cursor: 'pointer' }}>Remove</button>
              </div>
        )}
      </div>
    </div>
  );
}

function ComposeModal({ state, setState, walls, topics, isManager, user, onDone }) {
  const headers = { 'x-user-id': String(user.id) };
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState('wall');   // 'wall' | 'email'
  const [audOpts, setAudOpts] = useState(null);             // {everyone_count, roles, locations}
  const [customGroups, setCustomGroups] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);   // {html, recipient_count}
  const [newGroup, setNewGroup] = useState(null);           // {name, people:[], selected:Set}
  const s = state; const set = (k, v) => setState({ ...s, [k]: v });
  const previewHtml = renderBoardMarkup(s.body_md || '_Your message…_');

  // Load audience options + custom groups when email is on / wall changes.
  useEffect(() => {
    if (!s.emailed) return;
    const qs = s.location_id != null ? `?location_id=${s.location_id}` : '';
    api('GET', `/api/board/audience${qs}`, null, headers).then(setAudOpts).catch(() => setAudOpts(null));
    api('GET', '/api/board/groups', null, headers).then((r) => setCustomGroups(r.groups || [])).catch(() => {});
  }, [s.emailed, s.location_id]);

  const aud = s.email_audience || { type: 'everyone', roles: [], location_ids: [], custom_group_ids: [] };
  const setAud = (next) => set('email_audience', { type: 'everyone', roles: [], location_ids: [], custom_group_ids: [], ...aud, ...next });
  const toggleIn = (key, val) => { const set0 = new Set(aud[key] || []); set0.has(val) ? set0.delete(val) : set0.add(val); setAud({ type: 'groups', [key]: [...set0] }); };

  // Refresh the email preview when the email side is active + inputs change.
  useEffect(() => {
    if (!s.emailed || previewMode !== 'email') { setEmailPreview(null); return; }
    const t = setTimeout(() => {
      api('POST', '/api/board/email-preview', { location_id: s.location_id, title: s.title, body_md: s.body_md, email_audience: aud }, headers)
        .then(setEmailPreview).catch(() => setEmailPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [s.emailed, previewMode, s.title, s.body_md, s.location_id, JSON.stringify(aud)]);

  async function submit() {
    if (!s.title.trim()) { alert('Add a title.'); return; }
    setBusy(true);
    try {
      if (s.format === 'embed' && !(s.embed_html || '').trim()) { alert('Paste the page HTML, or switch to Simple message.'); setBusy(false); return; }
      const r = await api('POST', '/api/board/posts', {
        location_id: s.location_id, topic: s.topic, title: s.title, body_md: s.body_md,
        managers_only: s.managers_only, require_ack: s.require_ack,
        emailed: !!s.emailed, email_audience: s.emailed ? aud : null,
        format: s.format || 'markdown', embed_html: s.format === 'embed' ? s.embed_html : null,
      }, headers);
      if (s.emailed && r.emailed_to != null) setTimeout(() => alert(`Posted. Emailed ${r.emailed_to} ${r.emailed_to === 1 ? 'person' : 'people'}.`), 0);
      onDone(s.topic);
    } catch (e) { let m = e.message || 'Failed'; try { m = JSON.parse(m).error || m; } catch (_) {} alert(m); }
    finally { setBusy(false); }
  }

  async function openNewGroup() {
    const qs = s.location_id != null ? `?location_id=${s.location_id}` : '';
    const r = await api('GET', `/api/board/people${qs}`, null, headers);
    setNewGroup({ name: '', people: r.people || [], selected: new Set() });
  }
  async function saveNewGroup() {
    if (!newGroup.name.trim() || newGroup.selected.size === 0) { alert('Name the group and pick at least one person.'); return; }
    const r = await api('POST', '/api/board/groups', { name: newGroup.name.trim(), member_user_ids: [...newGroup.selected] }, headers);
    const cg = await api('GET', '/api/board/groups', null, headers); setCustomGroups(cg.groups || []);
    setAud({ type: 'groups', custom_group_ids: [...(aud.custom_group_ids || []), r.id] });
    setNewGroup(null);
  }

  return (
    <div onClick={(e) => e.target === e.currentTarget && setState(null)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,8,.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 680, width: '100%', margin: '24px auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>New post</div>
          <button onClick={() => setState(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, color: '#9e9e9e', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="board-compose-grid">
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <Field label="Wall"><select value={s.location_id == null ? '' : s.location_id} onChange={(e) => set('location_id', e.target.value === '' ? null : Number(e.target.value))} style={inp}>
                {walls.map((w) => <option key={w.key} value={w.location_id == null ? '' : w.location_id}>{w.label}</option>)}
              </select></Field>
              <Field label="Topic"><select value={s.topic} onChange={(e) => set('topic', e.target.value)} style={inp}>
                {topics.filter((t) => t !== 'For Managers' || isManager).map((t) => <option key={t}>{t}</option>)}
              </select></Field>
            </div>
            <Field label="Title"><input value={s.title} onChange={(e) => set('title', e.target.value)} placeholder="Short and clear" style={inp} /></Field>
            {isManager && (
              <div style={{ marginTop: 12, display: 'inline-flex', background: '#f4f1ea', border: '1px solid #e0d8c8', borderRadius: 999, padding: 2 }}>
                {[['markdown', 'Simple message'], ['embed', 'Embed a page']].map(([f, lbl]) => (
                  <button key={f} type="button" onClick={() => set('format', f)}
                    style={{ border: 'none', background: s.format === f ? 'var(--forest)' : 'transparent', color: s.format === f ? '#fff' : '#757575',
                              borderRadius: 999, fontSize: 12, fontWeight: 700, padding: '4px 12px', cursor: 'pointer' }}>{lbl}</button>
                ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <div style={fLabel}>{s.format === 'embed' ? 'Intro (optional)' : 'Message'}</div>
              <textarea value={s.body_md} onChange={(e) => set('body_md', e.target.value)}
                placeholder={s.format === 'embed' ? 'A line or two shown above the embedded page (and in the email).' : 'Write your message. **bold**, *italic*, [links](https://…), - bullets, blank line for a new paragraph.'}
                style={{ ...inp, minHeight: s.format === 'embed' ? 56 : 110, resize: 'vertical', lineHeight: 1.5 }} />
            </div>
            {s.format === 'embed' && (
              <div style={{ marginTop: 12 }}>
                <div style={fLabel}>Paste the page HTML</div>
                <textarea value={s.embed_html} onChange={(e) => set('embed_html', e.target.value)}
                  placeholder="Paste a self-contained HTML artifact. It renders on the board in an isolated frame (its own styles/scripts, sandboxed). The email links here — email can't show it inline."
                  style={{ ...inp, minHeight: 130, resize: 'vertical', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, lineHeight: 1.4 }} />
              </div>
            )}
            {isManager && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={optRow}><input type="checkbox" checked={!!s.emailed} onChange={(e) => set('emailed', e.target.checked)} /> <span><b>Also send as email</b> <span style={{ color: '#9e9e9e', fontSize: 12 }}>— posts to the wall AND hits inboxes.</span></span></label>
                <label style={optRow}><input type="checkbox" checked={s.require_ack} onChange={(e) => set('require_ack', e.target.checked)} /> <span><b>Require acknowledgment</b> <span style={{ color: '#9e9e9e', fontSize: 12 }}>— read-receipt button; you see who's read it.</span></span></label>
                <label style={optRow}><input type="checkbox" checked={s.managers_only} onChange={(e) => set('managers_only', e.target.checked)} /> <span><b>Managers only</b> <span style={{ color: '#9e9e9e', fontSize: 12 }}>— visible only to owners/managers.</span></span></label>
              </div>
            )}
            {isManager && s.emailed && (
              <div style={{ marginTop: 10, background: '#f4f1ea', borderRadius: 10, padding: 10 }}>
                <div style={fLabel}>Email to</div>
                <label style={{ ...optRow, marginTop: 4 }}><input type="radio" checked={aud.type === 'everyone'} onChange={() => setAud({ type: 'everyone' })} /> {s.location_id == null ? `Everyone (all cafés)${audOpts ? ' · ' + audOpts.everyone_count : ''}` : `Everyone at ${wallName(walls, s.location_id)}${audOpts ? ' · ' + audOpts.everyone_count : ''}`}</label>
                <label style={optRow}><input type="radio" checked={aud.type === 'groups'} onChange={() => setAud({ type: 'groups' })} /> Choose groups</label>
                {aud.type === 'groups' && audOpts && (
                  <div style={{ marginTop: 4 }}>
                    {audOpts.roles.length > 0 && <><div style={gsub}>Roles{s.location_id != null ? ' here' : ' (all cafés)'}</div>
                      {audOpts.roles.map((r) => <label key={r.key} style={gopt}><input type="checkbox" checked={(aud.roles || []).includes(r.key)} onChange={() => toggleIn('roles', r.key)} /> {r.label} · {r.count}</label>)}</>}
                    {audOpts.locations.length > 0 && <><div style={gsub}>Locations</div>
                      {audOpts.locations.map((l) => <label key={l.id} style={gopt}><input type="checkbox" checked={(aud.location_ids || []).includes(l.id)} onChange={() => toggleIn('location_ids', l.id)} /> {l.label} · {l.count}</label>)}</>}
                    <div style={gsub}>Custom groups</div>
                    {customGroups.map((g) => <label key={g.id} style={gopt}><input type="checkbox" checked={(aud.custom_group_ids || []).includes(g.id)} onChange={() => toggleIn('custom_group_ids', g.id)} /> {g.name} · {g.member_count}</label>)}
                    <button type="button" onClick={openNewGroup} style={{ ...miniBtn, borderStyle: 'dashed', color: 'var(--forest)', fontWeight: 700 }}>＋ New group</button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ background: '#f4f1ea', borderRadius: 10, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <div style={fLabel}>Preview</div>
              {s.emailed && <div style={{ marginLeft: 'auto', display: 'inline-flex', background: '#fff', border: '1px solid #d0d4d9', borderRadius: 999, padding: 2 }}>
                {['wall', 'email'].map((m) => <button key={m} onClick={() => setPreviewMode(m)} style={{ border: 'none', background: previewMode === m ? 'var(--forest)' : 'transparent', color: previewMode === m ? '#fff' : '#757575', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer' }}>{m === 'wall' ? 'On the wall' : 'As email'}</button>)}
              </div>}
            </div>
            {(!s.emailed || previewMode === 'wall') ? (
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--serif, Georgia)' }}>{s.title || '(title)'}</div>
                {s.body_html !== '' && <div className="board-body" style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: s.format === 'embed' ? renderBoardMarkup(s.body_md || '') : previewHtml }} />}
                {s.format === 'embed' && (s.embed_html || '').trim()
                  ? <div style={{ marginTop: 8 }}><EmbedFrame html={s.embed_html} title="Preview" /></div>
                  : s.format === 'embed' && <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 8 }}>Paste HTML to preview the embed.</div>}
              </div>
            ) : (
              <div>
                {emailPreview
                  ? <><div style={{ fontSize: 11, color: '#757575', marginBottom: 6 }}>To <b>{emailPreview.audience_label}</b> · {emailPreview.recipient_count} recipient{emailPreview.recipient_count !== 1 ? 's' : ''}</div>
                      <div style={{ border: '1px solid #e0d8c8', borderRadius: 8, overflow: 'hidden', background: '#fff' }} dangerouslySetInnerHTML={{ __html: emailPreview.html }} /></>
                  : <div style={{ fontSize: 12, color: '#9e9e9e' }}>Rendering email…</div>}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
          {s.emailed && emailPreview && <span style={{ marginRight: 'auto', fontSize: 12, color: '#757575' }}>Emails {emailPreview.recipient_count} · posts to {wallName(walls, s.location_id)}</span>}
          <button onClick={() => setState(null)} className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary">{busy ? 'Posting…' : (s.emailed ? 'Post & send email' : 'Post to the wall')}</button>
        </div>
      </div>
      {newGroup && (
        <div onClick={(e) => e.target === e.currentTarget && setNewGroup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,8,.4)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 380, width: '100%', margin: '40px auto', padding: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>New group</div>
            <input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} placeholder="Group name (e.g. Openers)" style={{ ...inp, marginBottom: 8 }} />
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
              {newGroup.people.map((p) => (
                <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', fontSize: 13 }}>
                  <input type="checkbox" checked={newGroup.selected.has(p.id)} onChange={() => { const sel = new Set(newGroup.selected); sel.has(p.id) ? sel.delete(p.id) : sel.add(p.id); setNewGroup({ ...newGroup, selected: sel }); }} />
                  {p.name} <span style={{ color: '#9e9e9e', fontSize: 11 }}>· {p.role}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button onClick={() => setNewGroup(null)} className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
              <button onClick={saveNewGroup} className="btn-primary">Save group ({newGroup.selected.size})</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function wallName(walls, locationId) { return locationId == null ? 'General' : (walls.find((w) => w.location_id === locationId)?.label || 'wall'); }

function AckModal({ data, user, onClose }) {
  const read = data.roster.filter((r) => r.read).length;
  const unread = data.roster.length - read;
  const [nudging, setNudging] = useState(false);
  async function nudge() {
    setNudging(true);
    try { const r = await api('POST', `/api/board/posts/${data.post.id}/nudge`, {}, { 'x-user-id': String(user.id) }); alert(`Nudged ${r.nudged} ${r.nudged === 1 ? 'person' : 'people'} who hadn't read it.`); }
    catch (e) { let m = e.message || 'Failed'; try { m = JSON.parse(m).error || m; } catch (_) {} alert(m); }
    finally { setNudging(false); }
  }
  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,8,.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 420, width: '100%', margin: '32px auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Who's read it</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, color: '#9e9e9e', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, color: '#757575', marginBottom: 10 }}>{read} of {data.roster.length} acknowledged “{data.post.title}”.</div>
          {data.roster.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
              {r.name}<span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 11, color: r.read ? '#2e7d32' : '#a55c33' }}>{r.read ? '✓ Read' : 'Not yet'}</span>
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 13 }}>Close</button>
          <button onClick={nudge} disabled={nudging || unread === 0} className="btn-primary">{nudging ? 'Sending…' : `✉ Nudge who hasn't (${unread})`}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><div style={fLabel}>{label}</div>{children}</div>; }
function rel(ts) {
  const d = new Date(ts); const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now'; if (m < 60) return m + 'm ago'; if (m < 1440) return Math.round(m / 60) + 'h ago'; return Math.round(m / 1440) + 'd ago';
}
const inp = { width: '100%', border: '1px solid #d0d4d9', borderRadius: 9, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: '#212121', boxSizing: 'border-box' };
const fLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: '#9e9e9e', marginBottom: 4 };
const optRow = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 };
const miniBtn = { border: '1px solid #e0e0e0', background: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', color: '#555' };
const gsub = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9e9e9e', margin: '8px 0 3px' };
const gopt = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, margin: '2px 12px 2px 0' };
